import * as moment from 'moment-timezone';
import * as _ from 'underscore';

import { refreshSessionInfo } from '../../app/actions/RefreshSessionInfoAction';
import { incrementRouterSpaceReactKey } from '../../app/actions/spaces/IncrementRouterSpaceReactKeyAction';
import { clearMetricsDataCache } from '../../app/actions/spaces/MetricsDataCacheActions';
import { showRouterError } from '../../app/actions/spaces/RouterErrorActions';
import { appStore } from '../../app/AppStore';
import {
  CompositeSessionInfoSelectors, SessionServerSelectors,
  SessionSiteSelectors,
  SessionUserSelectors
} from '../../app/selectors/SessionInfoSelectors';
import { ServerUrls } from '../../app/ServerUrls';
import { LoginUrlsWrapper } from '../../app/spaces/Login/LoginUrls';
import { AppState } from '../../app/state/AppState';
import { Messages } from '../../codegen/Localize';
import { Tableau } from '../../common/utilities/TableauLog';
import { FeatureFlags } from '../FeatureFlags';
import { FeatureFlagsHelper } from '../FeatureFlagsHelper';
import { CommonErrorCode, LoginErrorCodes, SiteSwitchErrorCodes, SolrErrorCode, UserErrorCode } from '../helpers/ErrorCodes';
import { Helpers } from '../helpers/Helpers';
import { L10n } from '../L10n/L10n';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { ExtractEncryptionMode, Sites } from '../resources/Sites';
import { ClientCertificateLoginRequest } from '../ServerApi/requests/ClientCertificateLoginRequest';
import { CreateApiTokenRequest } from '../ServerApi/requests/CreateApiTokenRequest';
import { DeleteApiTokenRequest } from '../ServerApi/requests/DeleteApiTokenRequest';
import { GeneratePublicKeyRequest, IGeneratePublicKeyResult } from '../ServerApi/requests/GeneratePublicKeyRequest';
import { GetApiTokenRequest } from '../ServerApi/requests/GetApiTokenRequest';
import { GetFeatureValueRequest } from '../ServerApi/requests/GetFeatureValueRequest';
import { GetServerSettingsUnauthenticatedRequest } from '../ServerApi/requests/GetServerSettingsUnauthenticatedRequest';
import { GetSessionInfoRequest, IGetSessionInfoResult } from '../ServerApi/requests/GetSessionInfoRequest';
import { GetSiteSamlLoginInfoRequest } from '../ServerApi/requests/GetSiteSamlLoginInfoRequest';
import {
  InitializeServerWithActiveDirectoryUserRequest,
} from '../ServerApi/requests/InitializeServerWithActiveDirectoryUserRequest';
import { InitializeServerWithExternalUserRequest } from '../ServerApi/requests/InitializeServerWithExternalUserRequest';
import { InitializeServerWithLocalUserRequest } from '../ServerApi/requests/InitializeServerWithLocalUserRequest';
import {
  InitializeUserNlpOnboardingStageRequest,
} from '../ServerApi/requests/InitializeUserNlpOnboardingStageRequest';
import { KerberosLoginRequest } from '../ServerApi/requests/kerberosLoginRequest';
import { LoginRequest } from '../ServerApi/requests/LoginRequest';
import { ILogoutResult, LogoutRequest } from '../ServerApi/requests/LogoutRequest';
import { RecordNavigationTimingRequest } from '../ServerApi/requests/RecordNavigationTimingRequest';
import { Request } from '../ServerApi/requests/Request';
import { SspiLoginRequest } from '../ServerApi/requests/SspiLoginRequest';
import { SwitchSiteRequest } from '../ServerApi/requests/SwitchSiteRequest';
import { TagRequest } from '../ServerApi/requests/TagRequest';
import {
  IUpdateUserLocalTimeZoneDataParams,
  UpdateUserLocalTimeZoneDataRequest,
} from '../ServerApi/requests/UpdateUserLocalTimeZoneDataRequest';
import { IError } from '../ServerApi/types/IError';
import { IFeatureFlagsDoNotUse } from '../ServerApi/types/IFeatureFlagsDoNotUse';
import { IFormDataPostRequestParams } from '../ServerApi/types/IFormDataPostRequestParams';
import { IGetSiteSamlLoginInfoResult } from '../ServerApi/types/IGetSiteSamlLoginInfoResult';
import { ILoginParams } from '../ServerApi/types/ILoginParams';
import { IOAuthCredentialTypeInfo } from '../ServerApi/types/IOAuthCredential';
import { IPodRedirectionError } from '../ServerApi/types/IPodRedirectionError';
import { IRequest } from '../ServerApi/types/IRequest';
import { IResponse, IResult, ITagResult } from '../ServerApi/types/IResponse';
import { IServer } from '../ServerApi/types/IServer';
import {
  AuthenticationType,
  DomainFamily,
  ICustomLogo,
  IPreloginCustomization,
  IServerCustomization,
  IServerSettingsUnauthenticated,
} from '../ServerApi/types/IServerSettingsUnauthenticated';
import { IServerVersionInfo, ISessionInfo, IUserSessionInfo, IUserTimeZone, NLPOnboardingStage } from '../ServerApi/types/ISessionInfo';
import { ISiteLoginParams } from '../ServerApi/types/ISiteLoginParams';
import { ISiteNameWithRole } from '../ServerApi/types/ISiteNameWithRole';
import { SiteRoles } from '../ServerApi/types/ISiteRole';
import { ITagError } from '../ServerApi/types/ITagError';
import { MobileAppService } from './MobileAppService';
import { ScriptLoadingService } from './ScriptLoadingService';
import { SessionService } from './SessionService';
import { IToast, ToasterService, ToastType } from './ToasterService';
import { WindowLocationService } from './WindowLocationService';

Module.instance().constant('ApiPrefix', 'vizportal/api/web/v1/');

Module.instance().constant('RestApiPrefix', 'vizportal/api/rest/v1/');


// Start at 7-digit numbers so these don't collide with server error codes
export enum ErrorReason {
  Canceled = 1000001,
  ResourceNotFound = 1000002,
  Unauthorized = 1000003,
  Unknown = 1000004
}


export enum AuthLevel {
  Anonymous,
  Authenticated,
  SiteAdmin,
  SelfOrAdmin,
  ServerAdmin
}

export enum SiteDefaultAuth {
  Local,
  ActiveDirectory,
  SAML,
  External
}

export let MaxRequestArrayLength = 1000;

export class ServerService implements IServer {

  private sessionInfo: ISessionInfo;
  private sessionInfoPromise: ng.IPromise<void>;
  private deferUntilSessionExists: ng.IDeferred<void>;
  private numSessionInfoRequests: number;
  private serverSettings: IServerSettingsUnauthenticated;
  private caseInsensitiveSiteUrlNames: Set<string>;
  private rebuildingSearchIndexToast: IToast;
  private publishingSamplesToast: IToast;
  private authenticationTypeOverride: AuthenticationType;

  public static $inject = [
    '$http',
    '$location',
    '$q',
    '$state',
    'ApiPrefix',
    'RestApiPrefix',
    'ScriptLoadingService',
    'SessionService',
    'ToasterService',
    'WindowLocationService',
    'MobileAppService'
  ];

  constructor(
    private $http: ng.IHttpService,
    private $location: ng.ILocationService,
    private $q: ng.IQService,
    private $state: ng.ui.IStateService,
    private apiPrefix: string,
    private restApiPrefix: string,
    private scriptLoadingService: ScriptLoadingService,
    private sessionService: SessionService,
    private toaster: ToasterService,
    private windowLocation: WindowLocationService,
    private mobileAppService: MobileAppService)
  {
    this.numSessionInfoRequests = 0;
    this.sessionInfoPromise = null;
    this.caseInsensitiveSiteUrlNames = new Set((siteUrlName: string) => siteUrlName.toLowerCase());
  }

  /**
   * Make a server request. If there is a request pending that might change the session,
   * wait until that request is finished before sending this one.
   */
  public sendRequest<TParams, TResult>(request: IRequest<TParams, TResult>): ng.IPromise<TResult> {
    // Wait until any pending session info request is resolved before attempting the request
    if (this.numSessionInfoRequests > 0) {
      return this.sessionInfoPromise.then(() => this.requestPost(request));
    }
    return this.requestPost(request);
  }

  /**
   * Make a server request using Tableau API gateway. If there is a request pending that might change the session,
   * wait until that request is finished before sending this one.
   */
  public sendTagRequest<TParams, TResult>(request: TagRequest<TParams, TResult>): ng.IPromise<TResult> {
    // Wait until any pending session info request is resolved before attempting the request
    if (this.numSessionInfoRequests > 0) {
      return this.sessionInfoPromise.then(() => this.requestPostViaTag(request));
    }
    return this.requestPostViaTag(request);
  }


  /**
   * Send a series of requests of the same type.
   * The results are combined using the given function.
   * If any request results in a broken promise,
   * then this method returns that broken promise.
   */
  public sendRequests<TItem, TParams, TResult>(
    requests: IRequest<TParams, TResult>[],
    combineResults: (results: TResult[]) => TResult
  ): ng.IPromise<TResult> {
    const sendNextRequest = (index: number, results: TResult[]): ng.IPromise<TResult> => {
      return this.sendRequest(requests[index]).then((result: TResult) => {
        results[index] = result;
        const nextIndex = index + 1;
        if (nextIndex < requests.length) {
          return sendNextRequest(nextIndex, results);
        } else {
          return combineResults(results);
        }
      });
    };
    return sendNextRequest(0, []);
  }

  /**
   * Like sendRequests, but sends the requests in parallel.
   */
  public sendRequestsParallel<TItem, TParams, TResult>(
    requests: IRequest<TParams, TResult>[],
    combineResults: (results: TResult[]) => TResult
  ): ng.IPromise<TResult> {
    const results: ng.IPromise<TResult>[] = requests.map(
      (request: IRequest<TParams, TResult>) => {
        return this.sendRequest(request);
      }
    );

    return this.$q.all(results).then((results: TResult[]) => {
      return combineResults(results);
    });
  }


  /**
   * Fire off a request *synchronously* (usually NOT what we want to do) and discard the result.
   *
   * This is useful to firing off a request in a window.unload event handler, as async requests
   * are usually aborted before the window closes. We use jQuery directly because Angular provides
   * no async:false mode in it's $http service.
   *
   * DO NOT USE this functionality unless you absolutely need synchronous AJAX, e.g. in a window.unload handler.
   */
  public fireAndForgetSynchronousRequest<TParams, TResult extends IResult>(request: IRequest<TParams, TResult>) {
    const url: string = this.apiPrefix + request.method;

    const headers: { [key: string]: any; } = {};
    headers[this.sessionService.getSessionCookieName()] = this.sessionService.getSessionId();

    const settings: JQueryAjaxSettings = {
      async: false,
      type: 'POST',
      contentType: 'application/json',
      data: JSON.stringify(request),
      headers: headers
    };

    $.ajax(url, settings);
  }

  public login(username: string, password: string, siteUrlName?: string): ng.IPromise<void> {
    return this.sessionInfoRequest(() => {
      return this.requestPost(new GeneratePublicKeyRequest())
        .then((generatePublicKeyResult: IGeneratePublicKeyResult) => {
          const loginParams: ILoginParams = {
            username: username,
            encryptedPassword: rsa.encrypt(password, generatePublicKeyResult.key),
            keyId: generatePublicKeyResult.keyId
          };
          if (_.isString(siteUrlName)) {
            loginParams.siteUrlName = siteUrlName;

          }
          const loginRequest = new LoginRequest(loginParams);
          return this.requestPost(loginRequest)
            .then((loginResult: ISessionInfo) => {
              if (loginResult.errors) {
                return this.$q.reject(loginResult.errors[0]);
              }
              return this.applySessionInfoInClient(loginResult);
            });
        });
    });
  }

  public siteSamlLogin(username: string): ng.IPromise<IGetSiteSamlLoginInfoResult> {
    const loginParams: ILoginParams = {
      username: username,
      encryptedPassword: null,
      keyId: null
    };
    return this.requestPost(new GetSiteSamlLoginInfoRequest(loginParams))
      .then((getSiteSamlLoginInfoResult: IGetSiteSamlLoginInfoResult) => {
        if (!getSiteSamlLoginInfoResult) {
          return this.$q.reject(UserErrorCode.UserNotSupportedGuestOperation);
        } else if (getSiteSamlLoginInfoResult.errors) {
          return this.$q.reject(getSiteSamlLoginInfoResult.errors[0]);
        }
        // handle success
        return this.$q.when(getSiteSamlLoginInfoResult);
      }, (error) => { // handle when http status code is not 2xx
        if (error) {
          return this.$q.reject(error);
        }
        return this.$q.reject(UserErrorCode.UserNotSupportedGuestOperation);
      });
  }


  public autoLogin(siteUrlName?: string): ng.IPromise<void> {
    return this.sessionInfoRequest(() => {
      const loginParams: ISiteLoginParams = {};
      if (_.isString(siteUrlName)) {
        loginParams.siteUrlName = siteUrlName;
      }
      const authType = this.authenticationType();
      let loginRequest: Request<ISiteLoginParams, IGetSessionInfoResult>;
      switch (authType) {
        case AuthenticationType.Sspi:
          loginRequest = new SspiLoginRequest(loginParams);
          break;
        case AuthenticationType.Kerberos:
          loginRequest = new KerberosLoginRequest(loginParams);
          break;
        case AuthenticationType.ClientCertificate:
        case AuthenticationType.ClientCertificateWithFallback:
          loginRequest = new ClientCertificateLoginRequest(loginParams);
          break;
      }
      // This must be GET to get around an IE "feature". See B144390
      return this.requestGet(loginRequest)
        .then((sessionInfo: ISessionInfo) => {
          if (sessionInfo.errors) {
            return this.$q.reject(sessionInfo.errors[0] && sessionInfo.errors[0].code);
          }
          return this.applySessionInfoInClient(sessionInfo);
        }, (reason: number) => {
          this.clearSessionInfo();
          if (reason === ErrorReason.Unauthorized || reason === ErrorReason.Unknown) {
            switch (authType) {
              case AuthenticationType.Sspi:
                  reason = LoginErrorCodes.SspiFailed;
                  break;
              case AuthenticationType.Kerberos:
                  reason = LoginErrorCodes.KerberosFailed;
                  break;
              case AuthenticationType.ClientCertificate:
              case AuthenticationType.ClientCertificateWithFallback:
                  reason = LoginErrorCodes.ClientCertificateNotFound;
                  break;
            }
          }
          return this.$q.reject(reason);
        });
    });
  }

  public initialize(username: string, password: string, displayName: string): ng.IPromise<void> {
    return this.sessionInfoRequest(() => {
      return this.requestPost(new GeneratePublicKeyRequest())
        .then((generatePublicKeyResult: IGeneratePublicKeyResult) => {
          const encryptedPassword = rsa.encrypt(password, generatePublicKeyResult.key);
          let initializeRequest: Request<ILoginParams, ISessionInfo>;

          if (this.isServerDomainFamilyActiveDirectory()) {
            initializeRequest = new InitializeServerWithActiveDirectoryUserRequest({
              username: username,
              encryptedPassword: encryptedPassword,
              keyId: generatePublicKeyResult.keyId
            });
          } else if (this.isServerDomainFamilyLocal()) {
            initializeRequest = new InitializeServerWithLocalUserRequest({
              username: username,
              encryptedPassword: encryptedPassword,
              keyId: generatePublicKeyResult.keyId,
              displayName: displayName
            });
          } else if (this.isServerDomainFamilyExternal()) {
            initializeRequest = new InitializeServerWithExternalUserRequest({
              username: username,
              encryptedPassword: encryptedPassword,
              keyId: generatePublicKeyResult.keyId
            });
          } else {
            throw new Error('Unknown domain family type. Current implementation only supports: AD, local, and external.');
          }
          return this.requestPost(initializeRequest)
            .then((initializeResult: ISessionInfo) => {
              if (initializeResult.errors) {
                return this.$q.reject(initializeResult.errors[0] && initializeResult.errors[0].code);
              }
              return this.applySessionInfoInClient(initializeResult);
            });
        });
    });
  }


  /**
   * Returned promise will be fulfilled. All rejections are handled.
   * If there is an error, the code will be returned in the fulfilled handler.
   */
  public refreshSessionInfo(checkForEqualSite: boolean = false): ng.IPromise<any> {
    const previousUserId = this.sessionUserId();
    return this.sessionInfoRequest(() => {
      return this.requestPost(new GetSessionInfoRequest())
        .then((result: IGetSessionInfoResult) => {
          if (!result) {
            this.clearSessionInfo('error getting session info');
            return null;
          }
          if (result.errors) {
            this.clearSessionInfo(result.errors[0] && result.errors[0].code + '');
            return result.errors[0] && result.errors[0].code;
          } else {
            // in certain cases we only want to update session info if the returned session info matches the current site.
            // TFSID:953116. Switching quickly between two tabs of different sites on same server leads to bugs.
            // This site equality check prevents that
            if (checkForEqualSite && SessionSiteSelectors.siteId(appStore.getState()) !== result.site.id) {
              return;
            }
            return this.applySessionInfoInClient(result)
              .then((result) => {
                this.reloadStateIfUserChanged(previousUserId);
                return result;
              });
          }
        }, (errorResult: any) => this.clearSessionInfo('error getting session info: ' + errorResult))
        .catch((reason: any) => {
          this.clearSessionInfo(reason);
          return reason;
        });
    });
  }

  public refreshServerSettings(): ng.IPromise<void> {
    return this.requestPost(new GetServerSettingsUnauthenticatedRequest())
      .then((result: IServerSettingsUnauthenticated) => {
        this.serverSettings = result;
      });
  }

  public hasCreatorTier(): boolean {
    return this.siteCapacityCheckEnabled() ? this.siteHasCreatorCapacity() : this.serverHasCreatorCapacity();
  }

  public hasViewerTier(): boolean {
    if (this.hasCreatorTier()) {
      return true;
    }
    return this.siteCapacityCheckEnabled() ? this.siteHasViewerCapacity() : this.serverHasViewerCapacity();
  }

  public overrideAuthenticationType(authType: AuthenticationType) {
    this.authenticationTypeOverride = authType;
  }

  public siteAuthDefaultLabel(): string {
    if (this.isAuthenticationSaml()) {
      return Messages.siteAuthenticationServerLevelAuthSAML();
    } else if (this.isServerDomainFamilyLocal()) {
      return Messages.localDomain() ;
    } else if (this.isServerDomainFamilyActiveDirectory()) {
      return Messages.activeDirectory();
    } else {
      return Messages.externalDomain();
    }
  }

  public siteAuthDefault(): string {
    if (this.isAuthenticationSaml()) {
      return SiteDefaultAuth[SiteDefaultAuth.SAML];
    } else if (this.isServerDomainFamilyLocal()) {
      return SiteDefaultAuth[SiteDefaultAuth.Local];
    } else if (this.isServerDomainFamilyActiveDirectory()) {
      return SiteDefaultAuth[SiteDefaultAuth.ActiveDirectory];
    } else {
      return SiteDefaultAuth[SiteDefaultAuth.External];
    }
  }

  /**
   * Loads all state needed to render the app
   * Returns a promise that fulfills with a code indicating bootstrap state
   */
  public bootstrapSession(): ng.IPromise<number> {
    let sessionInfoNoSessionReason: number = null;
    return this.$q.all([
      this.refreshServerSettings(),
      this.refreshSessionInfo()
    ]).then((results: any[]): ng.IPromise<any> => {
      // Try to deep link to the mobile app as soon as possible, to avoid the mobile browser's auth dialog popping up,
      // which prevents iOS Safari from showing the deep linking dialog "Open this page in Tableau".
      // Note: If we try to do this in run.ts as the first thing when we initialize, the redirects triggered by
      // the deep linking logic interfere with the completion of getSessionInfo() calls resulting in the user getting
      // signed out.
      // For more details and vizclient counterpart, see https://reviewboard.prod.tableautools.com/r/253096
      // Also, we need to get server settings first so as not to deep link if the setting has been turned off on server.
      if (this.isEnableDeepLinking()) {
        // MobileAppService instance is required here because of
        // https://gitlab.tableausoftware.com/browser-clients/vizportalclient/merge_requests/4897#note_1060085
        this.mobileAppService.tryDeepLinkToApp(this.windowLocation);
      }

      sessionInfoNoSessionReason = results[1];
      // Now that we have server settings and session info we can determine the language to display
      return this.updateLanguageAndLocaleInClient();
    }).then((): ng.IPromise<number> => {
      return this.$q.when(sessionInfoNoSessionReason);
    });
  }

  /**
   * Uses moment.js to guess client time zone and makes a request to update.
   */
  public updateUserTimeZone() {

    if (this.isUserSetTimeZone() != null) {
      // only guess the timezone once and do not override user set time zone
      return;
    }
    const date = new Date();

    const timeZoneId = moment.tz.guess();
    const updateUserLocalTimeZoneDataParams: IUpdateUserLocalTimeZoneDataParams = {
      offset: date.getTimezoneOffset(),
      timeZoneId: timeZoneId,
      isUserSetTimeZone: false // this is a system guess not a user action
    };
    const updateUserLocalTimeZoneDataRequest =
        new UpdateUserLocalTimeZoneDataRequest(updateUserLocalTimeZoneDataParams);
    this.requestPost(updateUserLocalTimeZoneDataRequest);
  }

  public initializeUserNlpOnboardingStage() {
    if(this.shouldInitializeUserNlpOnboardingStage()) {
      this.requestPost(new InitializeUserNlpOnboardingStageRequest({nlpOnboardingStage: 'None'}));
    }
  }

  /**
   * This won't handle errors. Use SiteSwitchService.switchIfNeeded
   * to get the proper error feedback
   */
  public switchSiteIfNeeded_DoNotCallDirectly(urlName: string, podRedirectPath?: string): ng.IPromise<any> {
    return this.sessionInfoRequest(() => {
      // falsy values should be empty string for default site
      urlName = urlName || '';

      if (!this.isCurrentSiteUrlName(urlName)) {

        return this.requestPost(new SwitchSiteRequest({urlName : urlName}))
          .then((sessionInfo: ISessionInfo) => {
            if (sessionInfo && sessionInfo.errors) {
              const error = sessionInfo.errors[0];
              if (error && error.code === SiteSwitchErrorCodes.AttemptedToSwitchSiteToCurrentSite) {
                // No-op, do nothing
                // Remember that this particular site url name is case insensitive
                this.caseInsensitiveSiteUrlNames.add(urlName);
                this.refreshSessionInfo(); // TFSID: 503672 refresh in case stale data caused this
                return;
              }
              return this.$q.reject(error);
            }
            this.applySessionInfoInClient(sessionInfo);
            appStore.dispatch(clearMetricsDataCache());
          }, (error: IError) => {
            if (error && error.code === CommonErrorCode.RedirectToOtherPod) {
              this.redirectToOtherPod(error, podRedirectPath);
              // If redirected, defer indefinitely, to prevent further execution here
              return this.$q.defer().promise;
            }
          });
      }
      return this.$q.when(null);
    });
  }

  public recordNavigationTiming(timing: PerformanceTiming): void {
    //==== IE8 bug workaround: ====
    // Convert to plain ol' object with only the numbers from the browser's PerformanceTiming type.
    // Needed in order to send in request body in IE8.
    let timingObj = _.clone(timing);
    // For non IE8 browsers, remove functions (e.g. toJSON) so the browser doesn't attempt to serialize them.
    timingObj = _.omit(timingObj, _.functions(timingObj));

    this.requestPost(new RecordNavigationTimingRequest({timing: timingObj}));
  }

  public redirectToOtherPod(error: IError, destinationPodPath: string) : void {
    const destinationPodUrl: string = this.getDestinationPodFromError(error);
    const dest = destinationPodUrl + destinationPodPath;
    this.windowLocation.navigateToWithoutAddingToHistory(dest);
  }

  public isCurrentSiteUrlName(urlName: string): boolean {
    if (this.caseInsensitiveSiteUrlNames.contains(urlName)) {
      return this.sessionSiteUrlName().toLowerCase() === urlName.toLowerCase();
    }

    return this.sessionSiteUrlName() === urlName;
  }

  // username and domainName are only needed for checking SELF
  public isUserAuthorized(role: AuthLevel, userName: string = '', domainName: string  = ''): boolean {
    switch (role) {
      case AuthLevel.Anonymous:
      default:
        return true;
      case AuthLevel.Authenticated:
        return this.inSession();
      case AuthLevel.SiteAdmin:
        return this.isSessionSiteOrServerAdmin();
      case AuthLevel.SelfOrAdmin:
        // for the user place page, return true if an admin or the URL contains the
        // username and domainName of the session user
        return this.isSessionSiteOrServerAdmin() || this.isSessionUserSelf(userName, domainName);
      case AuthLevel.ServerAdmin:
        return this.isSessionServerAdmin();
    }
  }

  public signOut() {
    this.requestPost(new LogoutRequest())
      .then((logoutResult: ILogoutResult) => {
        // This causes all scripts to reload, so all client side app state will be cleared.
        this.windowLocation.navigateTo(logoutResult.redirectUrl || '/');
      });
  }

  /**
   * This is a convenience method to get the app state for the various session info selectors below
   * as we port their sources of truth to `SessionInfoSelectors.tsx` over time.
   */
  private appState(): AppState {
    return appStore.getState();
  }

  /**
   * Returns the entire session info object. If using this, be careful not to cache it; it's better
   * to use the getter methods below to fetch individual pieces each time so we know it's not stale.
   */
  public sessionInfo_useWithCare(): ISessionInfo {
    return this.sessionInfo;
  }

  public sessionUserInfo(): IUserSessionInfo {
    return this.sessionInfo && this.sessionInfo.user;
  }

  public isViewerUser() : boolean {
    return SessionSiteSelectors.siteRole(this.appState()) === SiteRoles.Viewer;
  }

  public isSessionRestrictedTrustedTicket(): boolean {
    return this.sessionInfo && this.sessionInfo.isRestrictedTrustedTicketSession;
  }

  public sessionUserDisplayName(): string {
    return this.sessionInfo && this.sessionInfo.user && this.sessionInfo.user.displayName || '';
  }

  public sessionUserId(): string {
    return SessionUserSelectors.sessionUserId(this.appState());
  }

  public sessionAuthUserId(): string {
    return this.sessionInfo && this.sessionInfo.user && this.sessionInfo.user.authId;
  }

  public sessionSiteLuid(): string {
    return this.sessionInfo && this.sessionInfo.site && this.sessionInfo.site.luid;
  }

  public sessionUsername(): string {
    return this.sessionInfo && this.sessionInfo.user && this.sessionInfo.user.username;
  }

  public sessionUserDomainName(): string {
    return this.sessionInfo && this.sessionInfo.user && this.sessionInfo.user.domainName;
  }

  public inSession(): boolean {
    return !!this.sessionService.isSessionCurrentlyValid();
  }

  public isPasswordResetEnabled(): boolean {
    return this.serverSettings && this.serverSettings.passwordResetEnabled;
  }

  public isDataAlertsEnabled(): boolean {
    return SessionSiteSelectors.isDataAlertsEnabled(this.appState());
  }

  public catalogingOnSiteEnabled(): boolean {
    return FeatureFlags.metadataAreaEnabled &&
      this.sessionInfo && this.sessionInfo.site && this.sessionInfo.site.catalogingOnSiteEnabled !== false;
  }

  public catalogingOnServerEnabled(): boolean {
    return FeatureFlags.metadataAreaEnabled &&
      this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.catalogingOnServerEnabled !== false;
  }

  public flowsOnSiteEnabled(): boolean {
    return this.sessionInfo && this.sessionInfo.site && this.sessionInfo.site.flowsOnSiteEnabled !== false;
  }

  public flowsOnServerEnabled(): boolean {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.flowsOnServerEnabled !== false;
  }

  public get isAfeUiSettingEnabled(): boolean {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.useAlternativeFederationEngine === true;
  }

  public canSessionUserEditEmbeddedCredentials(): boolean {
    return this.isSessionSiteOrServerAdmin() || (this.sessionInfo && this.sessionInfo.server.embeddedCredentialsEnabled);
  }

  public isSessionUserSelf(userName: string, domainName: string): boolean {
    return this.sessionInfo && this.sessionInfo.user
      && this.sessionInfo.user.username === userName
      && this.sessionInfo.user.domainName === domainName;
  }

  public isSessionServerAdmin(): boolean {
    return SessionUserSelectors.isSessionServerAdmin(this.appState());
  }

  public isSessionSiteAdmin(): boolean {
    return SessionSiteSelectors.isSessionSiteAdmin(this.appState());
  }

  public isSessionSiteOrServerAdmin(): boolean {
    return CompositeSessionInfoSelectors.currentUserIsSiteOrServerAdminSelector(this.appState());
  }

  public sessionSiteId(): string {
    return SessionSiteSelectors.siteId(this.appState());
  }

  public sessionSiteRole(): string {
    return SessionSiteSelectors.siteRole(this.appState());
  }

  public sessionServerId(): string {
    return this.sessionInfo ? this.sessionInfo.server.logicalServerInstanceId : '';
  }

  public sessionSite(): ISiteNameWithRole {
    return this.sessionInfo && this.sessionInfo.site;
  }

  public sessionSiteUrlName(): string {
    return SessionSiteSelectors.siteUrlName(this.appState());
  }

  public sessionSiteName(): string {
    return SessionSiteSelectors.siteName(this.appState());
  }

  public sessionServerVersion(): IServerVersionInfo {
    return this.sessionInfo ? this.sessionInfo.server.version : null;
  }

  public sessionServerPodId(): string {
    return this.sessionInfo ? this.sessionInfo.server.podId : null;
  }

  public sessionSupportHost(): string {
    return this.sessionInfo && this.sessionInfo.server.supportHost;
  }

  public sessionSupportPath(): string {
    return this.sessionInfo && this.sessionInfo.server.supportPath;
  }

  public isSessionMultiSite(): boolean {
    return this.sessionInfo && this.sessionInfo.user && this.sessionInfo.user.numberOfSites > 1;
  }

  public isBuyLicensesEnabled(): boolean {
    return this.sessionInfo && this.sessionInfo.site.buyLicensesEnabled;
  }

  public isVersioningEnabled(): boolean {
    return SessionSiteSelectors.isVersioningEnabled(this.appState());
  }

  public siteLogo(): ICustomLogo {
    return this.sessionInfo && this.sessionInfo.site && this.sessionInfo.site.customHeaderLogo;
  }

  public compactLogo(): ICustomLogo {
    return this.sessionInfo && this.sessionInfo.site && this.sessionInfo.site.customCompactLogo;
  }

  public isSiteCustomizationEnabled(): boolean {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.siteCustomizationEnabled;
  }

  public isTabAdminServiceEnabled(): boolean {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.tabAdminServiceEnabled;
  }

  public isRecommendationsEnabled(): boolean {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.recommendationsEnabled;
  }

  public isVizRecommendationsEnabled(): boolean {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.vizRecommendationsEnabled;
  }

  public isSiteAdminSettingsEnabled(): boolean {
    return SessionServerSelectors.siteAdminSettingsEnabled(this.appState());
  }

  /**
   * Returns a promise that resolves when all pending session info requests are completed.
   * This can be used in places where we don't want to get properties from the session when we know
   * they might be changing.
   * NOTE: This promise does not guarantee that there is a session when resolved; it only guarantees
   * that there are no in-progress pending API requests to fetch the session info. If you need to wait
   * for an actual session (e.g. at the login screen), use waitForSession() instead.
   */
  public getSessionInfoPromise(): ng.IPromise<void> {
    return this.sessionInfoPromise ? this.sessionInfoPromise : this.$q.when();
  }

  /**
   * Returns a promise that resolves when all pending session info requests are completed, AND
   * when there is a valid session. i.e. the promise will not resolve until after the user logs in.
   * This is different than getSessionInfoPromise(), which only looks at pending session info
   * requests and may resolve without a valid session.
   */
  public waitForSession(): ng.IPromise<void> {
    if (this.inSession()) {
      return this.getSessionInfoPromise();
    }

    if (!this.deferUntilSessionExists) {
      this.deferUntilSessionExists = this.$q.defer();
    }
    return this.deferUntilSessionExists.promise;
  }

  public sessionLanguage(): string {
    return this.sessionInfo && this.sessionInfo.user && this.sessionInfo.user.language;
  }

  public sessionLocale(): string {
    return this.sessionInfo && this.sessionInfo.user && this.sessionInfo.user.locale;
  }

  public updateDisplayNameAllowed(): boolean {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.updateDisplayNameAllowed;
  }

  public defaultSubscriptionsEmail(): string {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.defaultSubscriptionsEmail;
  }

  public featureFlagsDoNotUse(): IFeatureFlagsDoNotUse {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.featureFlags;
  }

  public changePasswordUrl(): string {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.changePasswordUrl;
  }

  public datasourceHelpUrl(): string {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.datasourceHelpUrl;
  }

  public offlineHelpEnabled(): boolean {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.offlineHelpEnabled;
  }

  public helpEdition(): string {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.helpEdition;
  }

  public oauthSettings() : Array<IOAuthCredentialTypeInfo> {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.oauthSettings;
  }

  public rebuildingSearchIndex(): boolean {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.rebuildingSearchIndex;
  }

  public publishingSamples(): boolean {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.publishingSamples;
  }

  public defaultDomain(): string {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.defaultDomain;
  }

  public isBetaPod(): boolean {
    return this.sessionInfo && this.sessionInfo.user && this.sessionInfo.server.betaPod;
  }

  public canSessionUserAuthorOnWeb(): boolean {
    return SessionUserSelectors.canAuthorOnWeb(this.appState());
  }

  public canSessionUserManageUsers(): boolean {
    return this.sessionInfo && this.sessionInfo.user && this.sessionInfo.user.canManageUsers;
  }

  public domainFamily(): string {
    return this.serverSettings && this.serverSettings.domainFamily;
  }

  public isServerDomainFamilyActiveDirectory(): boolean {
    return this.serverSettings && this.serverSettings.domainFamily === DomainFamily.ActiveDirectory;
  }

  public isServerDomainFamilyLocal(): boolean {
    return this.serverSettings && this.serverSettings.domainFamily === DomainFamily.Local;
  }

  public isServerDomainFamilyExternal(): boolean {
    return this.serverSettings && this.serverSettings.domainFamily === DomainFamily.External;
  }

  public getSamlServiceEntityId(): string {
    return this.serverSettings && this.serverSettings.samlServiceEntityId;
  }

  public getStartPage(): string {
    return this.sessionInfo && this.sessionInfo.user && this.sessionInfo.user.startPage || '/';
  }

  public userTimeZone(): IUserTimeZone {
    return this.sessionInfo && this.sessionInfo.user && this.sessionInfo.user.userTimeZone;
  }

  public isUserSetTimeZone(): boolean {
    return this.sessionInfo && this.sessionInfo.user && this.sessionInfo.user.isUserSetTimeZone;
  }

  public nlpOnboardingStage(): string | undefined {
    return this.sessionInfo && this.sessionInfo.user && this.sessionInfo.user.userNlpOnboardingStage;
  }

  public shouldInitializeUserNlpOnboardingStage(): boolean {
    return !this.isSessionRestrictedTrustedTicket() && this.nlpOnboardingStage() === undefined;
  }

  public isAdminViewsDisabled(): boolean {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.adminViewsDisabled;
  }

  public isAdminDataSourceEnabled(): boolean {
    return this.sessionInfo && this.sessionInfo.site && this.sessionInfo.site.adminInsightsEnabled;
  }

  private siteHasCreatorCapacity(): boolean {
    return this.sessionInfo && this.sessionInfo.site && this.sessionInfo.site.serverSiteCapacityInfo && this.sessionInfo.site.serverSiteCapacityInfo.hasCreatorCapacity;
  }

  private siteHasViewerCapacity(): boolean {
    return this.sessionInfo && this.sessionInfo.site && this.sessionInfo.site.serverSiteCapacityInfo && this.sessionInfo.site.serverSiteCapacityInfo.hasViewerCapacity;
  }

  // Server Settings

  private serverHasCreatorCapacity(): boolean {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.serverSiteCapacityInfo && this.sessionInfo.server.serverSiteCapacityInfo.hasCreatorCapacity;
  }

  private serverHasViewerCapacity(): boolean {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.serverSiteCapacityInfo && this.sessionInfo.server.serverSiteCapacityInfo.hasViewerCapacity;
  }

  public authenticationType(): AuthenticationType {
    return this.authenticationTypeOverride || this.serverSettings && this.serverSettings.authenticationType && this.serverSettings.authenticationType.type;
  }

  public licenseExpirationDateIfWithinWarningPeriod(): string {
    return this.serverSettings && this.serverSettings.licenseExpirationDate;
  }

  public siteCapacityCheckEnabled(): boolean {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.siteCapacityCheckEnabled;
  }

  public shouldShowServerLicensingTierInfo(): boolean {
    return this.isSessionServerAdmin();
  }

  public hasSubscriptionTieredLicensing(): boolean {
    return this.hasCreatorTier() || this.hasViewerTier();
  }

  public serverCustomization(): IServerCustomization {
    return this.serverSettings && this.serverSettings.customization;
  }

  public serverName(): string {
    const customization = this.serverCustomization();
    return customization && customization.serverName || Messages.title();
  }

  public dsdPhoneSize(): number {
    return this.serverSettings && this.serverSettings.dsdPhoneSize || 0;
  }

  public dsdTabletSize(): number {
    return this.serverSettings && this.serverSettings.dsdTabletSize || 0;
  }

  public serverLanguage(): string {
    return this.serverSettings && this.serverSettings.defaultLanguage;
  }

  public serverLocale(): string {
    return this.serverSettings && this.serverSettings.defaultLocale;
  }

  public displaySchedulesInClientTimezone(): boolean {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.displaySchedulesInClientTimezone;
  }

  public displayScheduleDescriptionAsName(): boolean {
    return this.sessionInfo.server.displayScheduleDescriptionAsName;
  }

  public isGuestEnabled(): boolean {
    return this.serverSettings && this.serverSettings.guestEnabled;
  }

  public isRunNowEnabled(): boolean {
    return this.sessionInfo && this.sessionInfo.server &&  this.sessionInfo.server.runNowOnServerEnabled;
  }

  public isUISiteCreationEnabled(): boolean {
    // the siteAdminSettings are only enabled in Tableau Online, using them as a signal for being in Online
    return !(this.isSiteAdminSettingsEnabled());
  }

  public isOnlineEnvironment(): boolean {
    return this.getHelpEdition() === 'online';
  }

  public isNotificationEnabledForServer(): boolean {
    return this.sessionInfo.server.notificationEnabledForServer;
  }

  public isNotificationEnabledForSite(): boolean {
    return this.sessionInfo && this.sessionInfo.site && this.sessionInfo.site.notificationEnabled;
  }

  public isSiteInviteEmailEnabledForServer(): boolean {
    return this.sessionInfo && this.sessionInfo.server.siteInviteBasicEmailEnabled;
  }

  public isWelcomeChannelEnabledForUser(): boolean {
    return this.sessionInfo && this.sessionInfo.user && this.sessionInfo.user.showWelcomeScreen;
  }

  public isAddSupportUserEnabledForServer(): boolean {
    return this.sessionInfo.server.featureFlags.addSupportUserEnabled;
  }

  public isWgEnabled(): boolean {
    return this.serverSettings && this.serverSettings.wgEnabled;
  }

  public preloginCustomization(): IPreloginCustomization {
    return this.serverSettings && this.serverSettings.preloginCustomization;
  }

  public isSiteSamlEnabled(): boolean {
    return this.serverSettings && this.serverSettings.siteSamlEnabled;
  }

  public isSignInNoteEnabled(): boolean {
    return this.serverSettings && this.serverSettings.signInNoteEnabled;
  }

  public getSignInNoteText(): string {
    return this.serverSettings && this.serverSettings.signInNoteText;
  }

  public getSignInNoteUrl(): string {
    return this.serverSettings && this.serverSettings.signInNoteUrl;
  }

  public isEnableDeepLinking(): boolean {
    return this.serverSettings && this.serverSettings.enableDeepLinking;
  }

  public getFeatureValue(serviceName: string, defaultValue: boolean): ng.IPromise<any> {
    return this.sendRequest(new GetFeatureValueRequest(serviceName, String(defaultValue))).then((result) => {
      if (result.errors) {
        return this.$q.reject(result.errors[0]);
      } else {
        return this.$q.resolve(result);
      }
    });
  }

  public getApiTokens(): ng.IPromise<any> {
    return this.sendRequest(new GetApiTokenRequest()).then((result) => {
      if (result.errors) {
        return this.$q.reject(result.errors[0]);
      } else {
        return this.$q.resolve(result);
      }
    });
  }

  public createApiToken(clientName: string): ng.IPromise<any> {
    return this.sendRequest(new CreateApiTokenRequest(clientName)).then((result) => {
      if (result.errors) {
        return this.$q.reject(result.errors[0]);
      } else {
        return this.$q.resolve(result);
      }
    });
  }

  public deleteApiToken(tokenId: string): ng.IPromise<any> {
    return this.sendRequest(new DeleteApiTokenRequest(tokenId)).then((result) => {
      if (result.errors) {
        return this.$q.reject(result.errors[0]);
      } else {
        return this.$q.resolve(result);
      }
    });
  }

  public isJobManagementEnabled(): boolean {
    return FeatureFlags.jobManagementEnabled;
  }

  public hideAdminVizStatsForLoadTimes(): boolean {
    return this.serverSettings && this.serverSettings.hideAdminVizStatsForLoadTimes;
  }

  public hideAdminVizPerformanceofViews(): boolean {
    return this.serverSettings && this.serverSettings.hideAdminVizPerformanceOfViews;
  }

  public hideAdminVizPerformanceofFlowRuns(): boolean {
    return !(this.hideAdminVizPerformanceofFlowRunsOnSite() && this.flowsOnServerEnabled());
  }

  public hideAdminVizPerformanceofFlowRunsOnSite(): boolean {
    return !(this.flowsOnServerEnabled() && this.flowsOnSiteEnabled());
  }

  public isTableauIdSupported(): boolean {
    return this.serverSettings && this.serverSettings.tableauIdSupported;
  }

  public isConnectedClientsVisible(): boolean {
    return this.serverSettings && this.serverSettings.connectedClientsVisible;
  }

  public isBridgeEnabled(): boolean {
    return this.serverSettings && this.serverSettings.bridgeEnabled;
  }

  public isEmbeddedVizForSiteSamlSupported(): boolean {
    return this.serverSettings && this.serverSettings.embeddedVizForSiteSamlSupported;
  }

  public isSelfServiceScheduleEnabled(): boolean {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.featureFlags &&
      this.sessionInfo.server.featureFlags.selfServiceSchedulesEnabled;
  }

  public isDesktopSamlDisabled(): boolean {
    return this.serverSettings && this.serverSettings.desktopSamlDisabled;
  }

  public isExtractEncryptionModeDisabled() : boolean {
    return this.sessionInfo && this.sessionInfo.site && this.sessionInfo.site.extractEncryptionMode && this.sessionInfo.site.extractEncryptionMode === ExtractEncryptionMode.DISABLED;
  }

  public isLimitedUserVisibilityEnforced(): boolean {
    return CompositeSessionInfoSelectors.isLimitedUserVisibilityEnforced(this.appState());
  }

  public getExtractEncryptionModeForSite(): ExtractEncryptionMode {
    return this.sessionInfo && this.sessionInfo.site && this.sessionInfo.site.extractEncryptionMode;
  }

  public isExtractEncryptionEnabledOmniSwitch(): boolean {
    // Backend checks for 1) Feature Flags and 2) environment specific checkes. (e.g. online vs onprem) for populating sessionInfo.server.extractEncryptionEnabled
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.extractEncryptionEnabled === true;
  }

  public isNamedSharingEnabled() {
    // This will check and return site setting as well as this feature is controlled by FF and site setting and if current user is guest.
    return SessionSiteSelectors.isNamedSharingEnabled(this.appState());
  }

  public isRequestAccessEnabled() {
    return SessionSiteSelectors.isRequestAccessEnabled(this.appState());
  }

  public analyticsEndpoint(): string {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.analyticsEndpoint;
  }

  private reloadStateIfUserChanged(previousUserId: string) {
    const newUserId = this.sessionUserId();
    if (previousUserId && newUserId && newUserId !== previousUserId) {
      appStore.dispatch(incrementRouterSpaceReactKey());
    }
  }

  public ignoreExtractTaskPriority(): boolean {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.ignoreExtractTaskPriority;
  }

  public maxDescriptionLength(): number {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.maxDescriptionLength;
  }

  private getHelpEdition(): string {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.helpEdition;
  }

  /**
   * Requests that could change the session must make the request through this method
   */
  private sessionInfoRequest(makeRequest: () => ng.IPromise<any>): ng.IPromise<any> {
    if (this.numSessionInfoRequests > 0) {
      // If existing session info request, make this request once it is completed.
      this.sessionInfoPromise = this.sessionInfoPromise.then(makeRequest);
    } else {
      this.sessionInfoPromise = makeRequest();
    }
    this.numSessionInfoRequests++;
    this.sessionInfoPromise.finally(() => {
      this.numSessionInfoRequests--;
      if (this.deferUntilSessionExists && this.numSessionInfoRequests === 0 && this.inSession()) {
        this.deferUntilSessionExists.resolve();
      }
    });
    return this.sessionInfoPromise;
  }

  private getFirstErrorFromResponse(response: ng.IHttpPromiseCallbackArg<IResponse<any>>): IError {
    return response && response.data && response.data.result &&
      response.data.result.errors && response.data.result.errors[0];
  }

  private getCodeFromError(error: IError): number {
    return error && error.code;
  }

  private getDestinationPodFromError(error: IError): string {
    const podRedirectionError = <IPodRedirectionError>error;
    return podRedirectionError && podRedirectionError.destinationPodUrl;
  }

  private request<TResult extends IResult>(requestConfig: ng.IRequestConfig): ng.IPromise<TResult> {
    return this.$http(requestConfig)
      .then(
        (successResponse: ng.IHttpPromiseCallbackArg<IResponse<TResult>>) => {

          const error = this.getFirstErrorFromResponse(successResponse);
          const code: number = this.getCodeFromError(error);
          if (this.maybeHandleSearchServerDown(code)) {
            return this.$q.reject(code);
          }
          const location: string = this.getDestinationPodFromError(error);
          if (this.maybeHandleRedirectionToOtherPod(code, location)) {
            return this.$q.reject(error);
          }
          return successResponse.data && !_.isUndefined(successResponse.data.result)
            ? successResponse.data.result
            : null;
        },
        (errorResponse: ng.IHttpPromiseCallbackArg<IResponse<TResult>>) => {
          const reason: number = this.getCodeFromError(this.getFirstErrorFromResponse(errorResponse));
          if (this.maybeHandleSearchServerDown(reason)) {
            return this.$q.reject(reason);
          }
          if (errorResponse.status === 401) {
            this.handle401(reason);
          } else if (errorResponse.status === 429) {
            return errorResponse.data.result;
          }
          return this.$q.reject(reason || ErrorReason.Unknown);
        }
      );
  }

  private requestViaTag<TResult extends ITagResult>(requestConfig: ng.IRequestConfig): ng.IPromise<TResult> {
    return this.$http(requestConfig).then(
      (successResponse: ng.IHttpPromiseCallbackArg<TResult>) => {
          return successResponse.data;
      },
      (errorResponse: ng.IHttpPromiseCallbackArg<ITagError>) => {
        if (errorResponse.status === 401) {
          const reason: number = errorResponse.data.httpErrorCode;
          this.handle401(reason);
          return this.$q.reject(reason || ErrorReason.Unknown);
        } else {
          const error: ITagError = errorResponse.data;
          const result: TResult = { errors: [error] } as TResult;
          return result;
        }
      }
    );
  }

  private requestGet<TParams, TResult extends IResult>(request: IRequest<TParams, TResult>): ng.IPromise<TResult> {
    let queryParams = '';
    if (request.params) {

      const requestParamsAsQueryParams = Helpers.serialize(request.params as Object);
      if (requestParamsAsQueryParams) {
        queryParams = '?' + requestParamsAsQueryParams;
      }
    }
    return this.request<TResult>({
      method: 'GET',
      url: this.apiPrefix + request.method + queryParams
    });
  }

  private requestPost<TParams, TResult extends IResult>(request: IRequest<TParams, TResult>): ng.IPromise<TResult> {
    return this.request<TResult>({
      method: 'POST',
      url: this.apiPrefix + request.method,
      data: request
    });
  }

  private requestPostViaTag<TParams, TResult extends ITagResult>(request: TagRequest<TParams, TResult>): ng.IPromise<TResult> {
    return this.requestViaTag<TResult>({
      method: 'POST',
      url: request.url(),
      data: request.data()
    });
  }

  public requestPostFormDataToRestApi(params: IFormDataPostRequestParams): ng.IPromise<any> {
    const formData: FormData = new FormData();
    const formDataFields = params.formDataFields;
    for (const i in formDataFields) {
      const formDataField = formDataFields[i];
      const formDataFieldKey = Object.keys(formDataField)[0];
      formData.append(formDataFieldKey, formDataField[formDataFieldKey]);
    }
    return this.requestPostFormToRestApi(formData, params.method);
  }

  public requestPostFormToRestApi(formData: FormData, method: string): ng.IPromise<IResult>{
    return this.request({
      method: 'POST',
      // Do not serialize into JSON (which is Angular's default) so we can send binary data
      transformRequest: (data: any) => data,
      // Allow browser to choose Content-Type to get boundry needed for binary data
      // see: https://groups.google.com/d/msg/angular/MBf8qvBpuVE/krmd75giKJUJ
      headers: {'Content-Type': undefined},
      url: this.restApiPrefix + method,
      data: formData
    });
  }

  // Anonymous is an angular state concept that means the user doesn't
  // have to be authenticated to view this url.
  public isAnonymousUrl(url: string): boolean {
    const loginUrls = LoginUrlsWrapper.getInstance();
    return url.includes('/#/authenticate')
      || url.includes(loginUrls.signinPath())
      || url.includes(loginUrls.signinErorrCodePath(''))
      || url.includes(loginUrls.signedOutPath())
      || url.includes(loginUrls.siteSamlSignedOutPath())
      || url.includes(loginUrls.passwordResetUserNameBasePath())
      || url.includes(loginUrls.requestPasswordResetPath())
      || url.includes('/#/pkce/');
   }

  private handle401(reason: number) {
    // If 1...we are not in a state (i.e. app has not bootstrapped)
    // or 2...we are in an anonymous state
    // or 3...the api responds 'Permission Denied' (NOT session expired)
    if (!this.$state.current.name ||
        this.isAnonymousUrl(this.$location.url()) ||
      reason === CommonErrorCode.CallingUserInsufficientPermissions) {
      return;
    }

    if (this.isAuthenticationAutoLogin() || this.isAuthenticationExternal()) {
      // For these auth types we can't give an error message that the user's session expired
      // so just refresh the page and let vizportal re-bootstrap and re-authenticate
      this.windowLocation.reload();
    } else {
      // For other auth types we can give a "session expired" error message, so do that
      const href = LoginUrlsWrapper.getInstance().signinUrl({
        redirect: this.$location.url() || null,
        error: reason || CommonErrorCode.InvalidSession
      });

      // We must remove the hash so the browser does a full page load. The /signin route is a special
      // route that apache will rewrite to /#/signin with query params intact
      this.windowLocation.navigateTo(href.replace(/^#/, ''));
    }
  }

  private maybeHandleSearchServerDown(errorCode: number) {
    if (errorCode && errorCode === SolrErrorCode.SolrCommunicationError || errorCode === SolrErrorCode.SolrDisabled) {
      if (!this.isUserAuthorized(AuthLevel.ServerAdmin)) {
        LoginUrlsWrapper.navigateToSignInErrorCode('' + errorCode);
      } else if (this.$location.path() !== ServerUrls.getInstance().statusPath()) {
        appStore.dispatch(showRouterError('search-server-down'));
      }
      return true;
    } else {
      return false;
    }
  }

  private maybeHandleRedirectionToOtherPod(errorCode: number, destinationPodUrl: string): boolean {
    return errorCode && (errorCode === CommonErrorCode.RedirectToOtherPod) && (destinationPodUrl !== null);
  }

  public isAuthenticationAutoLogin(): boolean {
    return this.isWindowsCredentialsAutoLogin() ||
      this.authenticationType() === AuthenticationType.ClientCertificate ||
      this.authenticationType() === AuthenticationType.ClientCertificateWithFallback;
  }

  public isWindowsCredentialsAutoLogin(): boolean {
    return this.authenticationType() === AuthenticationType.Sspi ||
      this.authenticationType() === AuthenticationType.Kerberos;
  }

  public isAuthenticationSaml(): boolean {
    return this.authenticationType() === AuthenticationType.Saml;
  }

  public isAuthenticationOpenId(): boolean {
     return this.authenticationType() === AuthenticationType.OpenId;
  }

  public isAuthenticationExternal(): boolean {
    // This doesn't include TabId, which Tableau Server would consider external. Consider renaming the function later on.
    return this.isAuthenticationSaml() || this.isAuthenticationOpenId();
  }

  public getSessionIdleLimitSeconds() {
    // TODO: remove the 5 minute default when the backend implements this. B144748
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.sessionIdleLimitSeconds || 5 * 60;
  }

  private applySessionInfoInClient(newSessionInfo: ISessionInfo): ng.IPromise<any> {
    this.sessionInfo = newSessionInfo;
    this.sessionService.createSession();
    if (newSessionInfo && newSessionInfo.server) {
        FeatureFlagsHelper.overwriteFeatureFlagsFromServerFeatureFlags(newSessionInfo.server.featureFlags, FeatureFlags);
    }
    this.updateUserTimeZone();
    this.initializeUserNlpOnboardingStage();
    appStore.dispatch(refreshSessionInfo(newSessionInfo));
    return this.updateLanguageAndLocaleInClient().then(() => {
      this.showOrHideRebuildingSearchIndexToast();
      this.showOrHidePublishingSamplesToast();
    });
  }

  private clearSessionInfo(message?: string) {

    // If someone is waiting for a session, but we didn't have a session, let them keep waiting.
    // Clear out the defer only if we were already in a session, at which point any clients who were
    // waiting for a session would have already had their promise resolved.
    if (this.inSession() && this.deferUntilSessionExists) {
      this.deferUntilSessionExists.reject('Session is being cleared');
      this.deferUntilSessionExists = null;
    }
    this.sessionService.endSession(message || '');
    this.sessionInfo = null;
  }

  private showOrHideRebuildingSearchIndexToast() {
    if (this.inSession() && L10n.language && this.rebuildingSearchIndex()) {
      if (!this.rebuildingSearchIndexToast || this.rebuildingSearchIndexToast.isClosed) {
        this.toaster.pop({
          type: ToastType.Error,
          text: Messages.rebuildingSearchIndexMessage(),
        });
        this.rebuildingSearchIndexToast = this.toaster.toasts[this.toaster.toasts.length - 1];
      }
    } else {
      if (this.rebuildingSearchIndexToast) {
        this.toaster.close(this.rebuildingSearchIndexToast.id);
      }
    }
  }

  private showOrHidePublishingSamplesToast() {
    if (this.inSession() && L10n.language && this.publishingSamples()) {
      if (!this.publishingSamplesToast || this.publishingSamplesToast.isClosed) {
        this.toaster.pop({
          type: ToastType.Info,
          text: Messages.publishingSamplesMessage(),
          timeoutDelay: 60 * 1000, // 60 seconds
        });
        this.publishingSamplesToast = this.toaster.toasts[this.toaster.toasts.length - 1];
      }
    } else {
      if (this.publishingSamplesToast) {
        this.toaster.close(this.publishingSamplesToast.id);
      }
    }
  }

  private updateLanguageAndLocaleInClient(): ng.IPromise<any> {
    const fallbackLanguage = 'en';
    const fallbackLocale = 'en_US';
    const newLanguage = this.sessionLanguage() || this.serverLanguage() || fallbackLanguage;
    const newLocale = this.sessionLocale() || this.serverLocale() || fallbackLocale;

    const languagePromise = this.maybeUpdateLanguageInClient(newLanguage).catch(() => {
      Tableau.Logger().error(`Failed to read UI strings for '${newLanguage}'. Falling back to '${fallbackLanguage}'`);
      // TODO: ideally perhaps we'd revert the language update on the server here?
      this.maybeUpdateLanguageInClient(fallbackLanguage);
    });
    const localePromise = this.maybeUpdateLocaleInClient(newLocale).catch(() => {
      this.maybeUpdateLocaleInClient(fallbackLocale);
    });
    return this.$q.all([languagePromise, localePromise]);
  }

  private maybeUpdateLanguageInClient(newLanguage: string): ng.IPromise<any> {
    const oldLanguage = L10n.language;
    let languagePromise: ng.IPromise<any> = this.$q.when();
    const messageLocaleMap: { [legacyLanguageLocale: string]: string } = {
      de: 'de_DE',
      en: 'en_US',
      es: 'es_ES',
      fr: 'fr_FR',
      ga: 'ga_IE',
      it: 'it_IT',
      ja: 'ja_JP',
      ko: 'ko_KR',
      pt: 'pt_BR',
      zh: 'zh_CN',

      de_DE: 'de_DE',
      en_GB: 'en_GB',
      en_US: 'en_US',
      es_ES: 'es_ES',
      fr_FR: 'fr_FR',
      ga_IE: 'ga_IE',
      it_IT: 'it_IT',
      ja_JP: 'ja_JP',
      ko_KR: 'ko_KR',
      pt_BR: 'pt_BR',
      zh_CN: 'zh_CN',
      zh_TW: 'zh_TW'
    };

    if (newLanguage !== oldLanguage) {
      L10n.language = newLanguage;
      let messageLocaleId = messageLocaleMap[newLanguage];
      if (!messageLocaleId) {
        Tableau.Logger().error(`Unrecognized language '${newLanguage}'. Falling back to 'en_US'`);
        messageLocaleId = 'en_US';
        L10n.language = 'en';
      }
      languagePromise = this.getLocalizeMessages(messageLocaleId);
    }

    return languagePromise;
  }

  private maybeUpdateLocaleInClient(newLocale: string): ng.IPromise<any> {
    const oldLocale = L10n.locale;
    if (newLocale !== oldLocale) {
      L10n.locale = newLocale;
      return this.getLocalizeFormattersAndParsers(newLocale);
    }
    return this.$q.when();
  }

  private getLocalizeMessages(localeName: string): ng.IPromise<void> {
    const promise = this.scriptLoadingService.loadScript(`localize/messages.${localeName}.js?${BuildId}`);
    return promise;
  }

  private getLocalizeFormattersAndParsers(localeName: string): ng.IPromise<void> {
    const promise = this.scriptLoadingService.loadScript(`localize/formatters-and-parsers.${localeName}.js?${BuildId}`);
    return promise;
  }

  public uploadSAMLMetadata(file: File): ng.IPromise<IResult> {
    const formData = new FormData();
    formData.append('entity_descriptor', file);
    formData.append(this.sessionService.getSessionCookieName(), this.sessionService.getSessionId());
    return this.requestPostFormToRestApi(formData, 'updateIdentityProvider').then((result: IResult) => {
      if (result.errors) {
        return this.$q.reject(result.errors[0] && result.errors[0].code);
      } else {
        return result;
      }
    });
  }

  public isSandboxEnabledOnServer(): boolean {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.personalSandboxEnabledOnServer;
  }

  public isSandboxEnabledByDefaultForNewSites(): boolean {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.personalSandboxByDefaultEnabled;
  }
}

Module.instance().service('ServerService', ServerService);

/**
 * Adds a filter to allow pug files to use On-Prem vs Online specific messages
 * without polluting a controller's scope.
 *
 * tokens need to have `_online` and `_server` at the end.
 */
Module.instance().filter('selectByServerEnvironment', ['ServerService', '$filter', (server: ServerService, $filter: any) => {
  return (token: string) => {
    const environmentSpecificToken = server.isOnlineEnvironment() ? `${token}_online` : `${token}_server`;
    return $filter('translate')(environmentSpecificToken, null, null);
  };
}]);
